import React, { useCallback, useState } from "react";
import {
  Avatar,
  Box,
  FormControlLabel,
  Grid, IconButton,
  Switch, Typography,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import Dropzone, { type FileWithPath } from "react-dropzone";
import { DeleteOutline } from "@mui/icons-material";
import FormDialog from "../FormDialog";

interface Props {
  open: boolean,
  importing?: boolean,
  onApply: (file: Record<string, unknown>, type: string, activate: boolean) => void,
  onClose: () => void,
}

const styles = {
  dropzoneDialog: {
    width: "100%",
    border: "3px dotted",
    borderColor: (theme: Theme) => (theme.palette as any).borderColor,
    borderRadius: (theme: Theme) => (theme as any).borderRadius,
    textAlign: "center",
    cursor: "pointer",
    transition: "background .3s ease-in-out, border .3s ease-in-out, border-color .3s ease-in-out",
    "&:hover": {
      background: (theme: Theme) => (theme.palette as any).borderColor,
      border: "3px solid",
      borderColor: (theme: Theme) => theme.palette.common.white,
    },
    "&:focus": {
      outline: "none",
    },
  },
  browseButtonDialog: {
    padding: "20px 0",
    "& span": {
      color: (theme: Theme) => (theme.palette.text as any).titleColor,
    },
  },
  fileItem: {
    display: "flex",
    flexWrap: "nowrap",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 0",
  },
};

function ImportPageFd({
  open = false,
  importing = false,
  onApply,
  onClose,
}: Props) {
  const [activated, setActivated] = useState(false);
  const [selectedFile, setSelectedFile] = useState<FileWithPath | null>(null);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [selectedType, setSelectedType] = useState("");

  const resetDialog = () => {
    setSelectedFile(null);
    setSelectedFileName("");
    setSelectedType("");
    setActivated(false);
  };
  const handleRemoveFile = () => {
    resetDialog();
  };
  const handleClose = () => {
    onClose();
    resetDialog();
  };
  const handleApply = () => {
    if (selectedFile) {
      onApply(selectedFile as unknown as Record<string, unknown>, selectedType, activated);
    }
    resetDialog();
  };
  const onDrop = useCallback((acceptedFiles: Array<File>) => acceptedFiles.forEach((file: File) => {
    const {
      name,
    } = file;
    const typeFormated = name.split(".").slice(-1).pop();

    setSelectedFile(file);
    setSelectedFileName(name);
    setSelectedType(typeFormated || "");
  }), []);

  return (
    <FormDialog
      open={open}
      loading={importing}
      title="Import CSV"
      hideApply={!selectedFile?.name}
      onApply={handleApply}
      onClose={handleClose}
    >
      <Grid container spacing={2} width={1}>
        <Grid size={12}>
          <FormControlLabel
            data-testid="import-page-dialog-activate-page-switch-container"
            control={(
              <Switch
                checked={activated}
                onChange={(e) => setActivated(e.target.checked)}
                name="activatePage"
                color="primary"
                data-testid="import-page-dialog-activate-page-switch"
              />
            )}
            label="Activate"
          />
        </Grid>
        <Grid size={12}>
          <Dropzone
            onDrop={onDrop}
            accept={{ "text/csv": [".csv"] }}
          >
            {({ getRootProps, getInputProps }) => (
              <Box
                mb={2}
                data-testid="import-page-dropzone-dialog"
                {...getRootProps({
                  sx: styles.dropzoneDialog,
                })}
              >
                <input {...getInputProps()} />
                <Typography sx={styles.browseButtonDialog}>
                  Drag files here or
                  &nbsp;
                  <span>Browse</span>
                </Typography>
              </Box>
            )}
          </Dropzone>
          {
            selectedFile?.name && (
              <Box px={1} width={1}>
                <Grid
                  container
                  mb={1}
                  sx={styles.fileItem}
                >
                  <Grid size={1}>
                    <Avatar variant="rounded">
                      {selectedType}
                    </Avatar>
                  </Grid>
                  <Grid size={6}>
                    <Typography
                      noWrap
                      color="textPrimary"
                      title="File name"
                      variant="body2"
                    >
                      {selectedFileName}
                    </Typography>
                  </Grid>
                  <Grid size={1}>
                    <IconButton
                      onClick={() => handleRemoveFile()}
                      title="Remove file"
                      data-testid="import-page-dropzone-dialog-file-remove-btn"
                    >
                      <DeleteOutline />
                    </IconButton>
                  </Grid>
                </Grid>
              </Box>
            )
          }
        </Grid>
      </Grid>
    </FormDialog>
  );
}

export default ImportPageFd;
